import { useCallback, useState } from "react";

import { useSearchParams } from "react-router";
import { useDebouncedCallback } from "use-debounce";
import { useDeepCompareMemo } from "use-deep-compare";

import { SEARCH_DEBOUNCE_MS } from "app/constants";

import { sortModelToOrder } from "./sortModelToOrder";

import type { GridSortModel } from "@mui/x-data-grid-pro";

/**
 * Converts order string from URL to GridSortModel
 * Supports multiple sort fields in comma-separated format
 * Example: "field1,field2-,field3" -\> [\{field: "field1", sort: "asc"\}, \{field: "field2", sort: "desc"\}, \{field: "field3", sort: "asc"\}]
 */
function parseOrderToSortModel(order: string): GridSortModel | null {
  const sortItems = order
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  if (!sortItems.length) {
    return null;
  }

  return sortItems.map((item) => {
    const isDesc = item.endsWith("-");
    const field = isDesc ? item.slice(0, -1) : item;
    return { field, sort: isDesc ? "desc" : "asc" } as const;
  });
}

/**
 * Hook for client-side sortable grid with URL persistence and debounced updates
 * Supports multiple sort fields in comma-separated URL format
 * Provides immediate local updates with 200ms debounced URL parameter changes
 * @returns [sortModel from URL/local state, setSortModel function]
 *
 * @example
 * const [sortModel, setSortModel] = useClientSideSortableGrid();
 *
 * \<Table
 *   sortModel=\{sortModel || []\}
 *   onSortModelChange=\{setSortModel\}
 *   // ... other props
 * /\>
 *
 * // URL examples:
 * // ?sort=name (sort by name ascending)
 * // ?sort=name- (sort by name descending)
 * // ?sort=name,date- (sort by name ascending, then by date descending)
 */
export function useClientSideSortableGrid(): [
  GridSortModel | null,
  (sortModel: GridSortModel | null) => void,
] {
  const [searchParams, setSearchParams] = useSearchParams({});

  // Local state for immediate updates - undefined means no local state set yet
  const [localSortModel, setLocalSortModel] = useState<
    GridSortModel | null | undefined
  >(undefined);

  // Get sort model from URL
  const sortParam = searchParams.get("sort");
  const urlSortModel: GridSortModel | null = sortParam
    ? parseOrderToSortModel(sortParam)
    : null;

  // Use local state for immediate updates, fallback to URL state
  const sortModel: GridSortModel | null = useDeepCompareMemo(() => {
    return localSortModel !== undefined ? localSortModel : urlSortModel;
  }, [localSortModel, urlSortModel]);

  // Debounced function to update URL parameters with 200ms delay
  const debouncedSetUrlParams = useDebouncedCallback(
    (sortModel: GridSortModel | null) => {
      const newParams = new URLSearchParams(searchParams);
      const order = sortModelToOrder(sortModel);

      if (!order) {
        newParams.delete("sort");
      } else {
        newParams.set("sort", order);
      }

      setSearchParams(newParams, { replace: true });
    },
    SEARCH_DEBOUNCE_MS,
  );

  // Function to update sort model with immediate local updates and debounced URL updates
  const setSortModel = useCallback(
    (sortModel: GridSortModel | null) => {
      // Update local state immediately for better UX
      setLocalSortModel(sortModel);

      // Update URL with debouncing to avoid excessive history entries
      debouncedSetUrlParams(sortModel);
    },
    [setLocalSortModel, debouncedSetUrlParams],
  );

  return [sortModel, setSortModel];
}
